import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { Clock, UserPlus, Download } from "lucide-react";
import { exportToCsv } from "@/lib/exportCsv";

interface PreRegDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PreRegDetailDialog({ open, onOpenChange }: PreRegDetailDialogProps) {
  const { data: otpPending = [], isLoading: loadingOtp } = useQuery({
    queryKey: ["prereg-otp-pending"],
    queryFn: async () => {
      const { data } = await supabase
        .from("student_pre_registrations")
        .select("id, full_name, form_iv_index, course, class, otp_code, created_at")
        .eq("onboarding_status", "pre_registered")
        .not("otp_code", "is", null);
      return data ?? [];
    },
    enabled: open,
  });

  const { data: awaitingSignup = [], isLoading: loadingSignup } = useQuery({
    queryKey: ["prereg-awaiting-signup"],
    queryFn: async () => {
      const { data } = await supabase
        .from("student_pre_registrations")
        .select("id, full_name, form_iv_index, course, class, created_at")
        .eq("onboarding_status", "pre_registered")
        .is("otp_code", null);
      return data ?? [];
    },
    enabled: open,
  });

  const handleExport = () => {
    const headers = ["Name", "Form IV Index", "Course", "Class", "Status"];
    const rows = [
      ...otpPending.map((s: any) => [s.full_name || "", s.form_iv_index, s.course || "", s.class || "", "OTP Pending"]),
      ...awaitingSignup.map((s: any) => [s.full_name || "", s.form_iv_index, s.course || "", s.class || "", "Awaiting Signup"]),
    ];
    exportToCsv("pre-registrations.csv", headers, rows);
  };

  const StudentRow = ({ student, showOtp }: { student: any; showOtp?: boolean }) => (
    <div className="flex items-center justify-between py-2 border-b last:border-0 gap-2">
      <div className="min-w-0">
        <p className="text-sm font-medium truncate">{student.full_name || "No name"}</p>
        <p className="text-xs text-muted-foreground truncate">
          {student.form_iv_index} · {student.course || "N/A"} · {student.class || "N/A"}
        </p>
      </div>
      {showOtp ? (
        <Badge variant="outline" className="shrink-0 text-amber-600 border-amber-300 bg-amber-50">OTP Pending</Badge>
      ) : (
        <Badge variant="outline" className="shrink-0 text-muted-foreground">Not Started</Badge>
      )}
    </div>
  );

  const LoadingSkeleton = () => (
    <div className="space-y-3">
      {[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex flex-row items-center justify-between gap-2">
          <DialogTitle>Pre-Registration Status</DialogTitle>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={otpPending.length + awaitingSignup.length === 0} className="mr-8">
            <Download className="h-4 w-4 mr-1" /> CSV
          </Button>
        </DialogHeader>
        <Tabs defaultValue="otp" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="otp" className="gap-1"><Clock className="h-3.5 w-3.5" />OTP Pending ({otpPending.length})</TabsTrigger>
            <TabsTrigger value="signup" className="gap-1"><UserPlus className="h-3.5 w-3.5" />Awaiting Signup ({awaitingSignup.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="otp" className="overflow-y-auto flex-1 mt-2">
            {loadingOtp ? <LoadingSkeleton /> : otpPending.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No students with pending OTP</p>
            ) : (
              <div>{otpPending.map((s: any) => <StudentRow key={s.id} student={s} showOtp />)}</div>
            )}
          </TabsContent>
          <TabsContent value="signup" className="overflow-y-auto flex-1 mt-2">
            {loadingSignup ? <LoadingSkeleton /> : awaitingSignup.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No students awaiting signup</p>
            ) : (
              <div>{awaitingSignup.map((s: any) => <StudentRow key={s.id} student={s} />)}</div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
